import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import DashboardTable from '../../components/DashboardTable';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import CircularProgress from '@mui/joy/CircularProgress';
import Chip from '@mui/joy/Chip';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DashboardPageHeader from '../../components/DashboardPageHeader';
import PageStack from '../../components/PageStack';
import { LAYOUT } from '../../theme/layoutTokens';
import { productsApi, ProductSearchParams, ProductsResponse } from '../../api/products.api';
import { categoriesApi } from '../../api/categories.api';
import { brandsApi } from '../../api/brands.api';
import { Product } from '../../types/product';
import { Category } from '../../types/category';
import { Brand } from '../../types/brand';
import { debounce } from 'lodash';

const FILTER_ALL = '__all__';

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryReference, setSelectedCategoryReference] = useState<string | undefined>(undefined);
  const [selectedBrandReference, setSelectedBrandReference] = useState<string | undefined>(undefined);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadProducts(1, itemsPerPage);
    loadCategories();
    loadBrands();
  }, [itemsPerPage]);

  const debouncedSearch = debounce((filters: ProductSearchParams) => {
    searchProducts(filters);
  }, 300);

  useEffect(() => {
    const filters: ProductSearchParams = {
      categoryReference: selectedCategoryReference,
      brandReference: selectedBrandReference,
      page: currentPage,
      limit: itemsPerPage,
    };
    if (searchTerm && searchTerm.length >= 2) {
      filters.search = searchTerm;
    }
    debouncedSearch(filters);
  }, [searchTerm, selectedCategoryReference, selectedBrandReference, currentPage, itemsPerPage]);

  const loadProducts = async (page: number = 1, limit: number = 10) => {
    try {
      setLoading(true);
      const response = await productsApi.getAll(page, limit);
      setProducts(response.data);
      updatePagination(response);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchProducts = async (filters: ProductSearchParams) => {
    try {
      setLoading(true);
      const response = await productsApi.search(filters);
      setProducts(response.data);
      updatePagination(response);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePagination = (response: ProductsResponse) => {
    setCurrentPage(response.page);
    setTotalPages(response.lastPage);
    setTotalItems(response.total);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoriesApi.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadBrands = async () => {
    try {
      const data = await brandsApi.getAll();
      setBrands(data);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategoryReference(undefined);
    setSelectedBrandReference(undefined);
    setCurrentPage(1);
  };

  const handleDelete = async (productReference: string) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      try {
        await productsApi.delete(productReference);
        const filters: ProductSearchParams = {
          categoryReference: selectedCategoryReference,
          brandReference: selectedBrandReference,
          page: currentPage,
          limit: itemsPerPage,
        };
        if (searchTerm && searchTerm.length >= 2) {
          filters.search = searchTerm;
        }
        await searchProducts(filters);
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  if (loading && products.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const paginationItems: React.ReactNode[] = [];
  const paginationEllipsis = (key: string) => (
    <Typography key={key} level="body-sm" sx={{ px: 1.5, py: 1 }}>
      ...
    </Typography>
  );

  paginationItems.push(
    <Button
      key={1}
      size="sm"
      variant={currentPage === 1 ? 'solid' : 'outlined'}
      color={currentPage === 1 ? 'primary' : 'neutral'}
      onClick={() => handlePageChange(1)}
    >
      1
    </Button>,
  );

  if (totalPages > 1) {
    if (totalPages > 7) {
      if (currentPage > 4) {
        paginationItems.push(paginationEllipsis('e1'));
      }
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        paginationItems.push(
          <Button
            key={i}
            size="sm"
            variant={currentPage === i ? 'solid' : 'outlined'}
            color={currentPage === i ? 'primary' : 'neutral'}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Button>,
        );
      }
      if (currentPage < totalPages - 3) {
        paginationItems.push(paginationEllipsis('e2'));
      }
    } else {
      for (let i = 2; i < totalPages; i++) {
        paginationItems.push(
          <Button
            key={i}
            size="sm"
            variant={currentPage === i ? 'solid' : 'outlined'}
            color={currentPage === i ? 'primary' : 'neutral'}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Button>,
        );
      }
    }
    paginationItems.push(
      <Button
        key={totalPages}
        size="sm"
        variant={currentPage === totalPages ? 'solid' : 'outlined'}
        color={currentPage === totalPages ? 'primary' : 'neutral'}
        onClick={() => handlePageChange(totalPages)}
      >
        {totalPages}
      </Button>,
    );
  }

  return (
    <PageStack spacing={LAYOUT.stackSurfaceGap}>
      <DashboardPageHeader
        title="Productos"
        action={
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 1.5 }}>
            <Button component={RouterLink} to="/products/new" color="primary">
              Nuevo producto
            </Button>
          </Stack>
        }
      />

      <Sheet variant="outlined" sx={{ p: LAYOUT.cardPadding, borderRadius: 'md', boxShadow: 'sm' }}>
        <Typography level="title-sm" sx={{ mb: LAYOUT.stackTight, color: 'text.secondary', fontWeight: 'lg' }}>
          Filtros
        </Typography>
        <Grid container spacing={LAYOUT.gridSpacing}>
          <Grid xs={12} md={4}>
            <FormControl>
              <FormLabel>Buscar por nombre</FormLabel>
              <Input
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Mínimo 2 caracteres"
                startDecorator={<SearchRoundedIcon />}
              />
            </FormControl>
          </Grid>
          <Grid xs={12} md={4}>
            <FormControl>
              <FormLabel>Categoría</FormLabel>
              <Select
                value={selectedCategoryReference ?? FILTER_ALL}
                onChange={(_, value) => {
                  const v = value as string | number;
                  setSelectedCategoryReference(v === FILTER_ALL ? undefined : (v as string));
                  setCurrentPage(1);
                }}
              >
                <Option value={FILTER_ALL}>Todas las categorías</Option>
                {categories.map((c) => (
                  <Option key={c.reference} value={c.reference}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} md={4}>
            <FormControl>
              <FormLabel>Marca</FormLabel>
              <Select
                value={selectedBrandReference ?? FILTER_ALL}
                onChange={(_, value) => {
                  const v = value as string | number;
                  setSelectedBrandReference(v === FILTER_ALL ? undefined : (v as string));
                  setCurrentPage(1);
                }}
              >
                <Option value={FILTER_ALL}>Todas las marcas</Option>
                {brands.map((b) => (
                  <Option key={b.reference} value={b.reference}>
                    {b.name}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: LAYOUT.stackTight }}>
          <Button variant="outlined" color="neutral" onClick={clearFilters}>
            Limpiar filtros
          </Button>
        </Stack>
      </Sheet>

      {loading ? (
        <Typography level="body-sm" sx={{ textAlign: 'center', py: 2 }}>
          Buscando productos...
        </Typography>
      ) : null}

      <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'md', boxShadow: 'sm', p: 0 }}>
        {products.length === 0 ? (
          <Typography level="body-sm" sx={{ py: 4, textAlign: 'center', color: 'neutral.500' }}>
            No se encontraron productos.
          </Typography>
        ) : (
          <>
            <DashboardTable stickyHeader>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Categoría</th>
                  <th>Marca</th>
                  <th>Precio</th>
                  <th>Stock</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.reference}>
                    <td>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <RouterLink to={`/products/${product.reference}`} style={{ textDecoration: 'none' }}>
                          <Typography level="body-sm" sx={{ color: 'primary.500', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                            {product.name}
                          </Typography>
                        </RouterLink>
                        {product.productType === 'configurable' && product.children && product.children.length > 0 && (
                          <Chip size="sm" variant="soft" color="primary">
                            {product.children.length} variante{product.children.length !== 1 ? 's' : ''}
                          </Chip>
                        )}
                      </Stack>
                    </td>
                    <td>
                      <Typography level="body-sm" sx={{ fontSize: 'xs' }}>
                        {product.productType === 'configurable' ? 'Compuesto' : 'Único'}
                      </Typography>
                    </td>
                    <td>{product.category ? product.category.name : '—'}</td>
                    <td>{product.brand ? product.brand.name : '—'}</td>
                    <td>${product.price.toFixed(2)}</td>
                    <td>{product.quantity}</td>
                    <td>
                      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                        <IconButton
                          component={RouterLink}
                          to={`/products/${product.reference}/edit`}
                          size="sm"
                          variant="plain"
                          color="primary"
                        >
                          <EditRoundedIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleDelete(product.reference)}
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DashboardTable>

            <Box
              sx={{
                ...LAYOUT.tableFooterPadding,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: LAYOUT.stackTight,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography level="body-sm">
                  Mostrando {products.length} de {totalItems} resultados
                </Typography>
                <Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={(_, value) => {
                    if (value != null) {
                      setItemsPerPage(Number(value));
                      setCurrentPage(1);
                    }
                  }}
                >
                  <Option value={10}>10 por página</Option>
                  <Option value={25}>25 por página</Option>
                  <Option value={50}>50 por página</Option>
                </Select>
              </Stack>

              {totalPages > 1 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
                  <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    startDecorator={<ChevronLeftRoundedIcon />}
                  >
                    Anterior
                  </Button>
                  {paginationItems}
                  <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    endDecorator={<ChevronRightRoundedIcon />}
                  >
                    Siguiente
                  </Button>
                </Stack>
              ) : null}
            </Box>
          </>
        )}
      </Sheet>
    </PageStack>
  );
};

export default ProductList;
